import React, { useState, useEffect, useCallback } from 'react';
import styles from './BrandVisibilityCalendar.module.css';

// Demo data for fallback when no token available
const DEMO_ACTIVITIES = [
  {
    id: 1,
    title: 'Summer Collection Launch',
    type: 'campaign',
    status: 'completed',
    date: '2025-07-01',
    brand_id: 1,
    brand_name: 'EcoWear',
    results: { impressions: 45000, engagement: 8.5, conversions: 120 }
  },
  {
    id: 2,
    title: 'Sustainability Webinar',
    type: 'content',
    status: 'active',
    date: '2025-07-02',
    brand_id: 2,
    brand_name: 'GreenThreads',
    results: { impressions: 12000, engagement: 12.3, conversions: 45 }
  },
  {
    id: 3,
    title: 'Pop-up Store - NYC',
    type: 'event',
    status: 'scheduled',
    date: '2025-07-15',
    brand_id: 1,
    brand_name: 'EcoWear',
    results: { impressions: 0, engagement: 0, conversions: 0 }
  },
  {
    id: 4,
    title: 'Charity Run Sponsorship',
    type: 'partnership',
    status: 'completed',
    date: '2025-07-10',
    brand_id: 3,
    brand_name: 'PureEarth',
    results: { impressions: 78000, engagement: 15.2, conversions: 230 }
  },
  {
    id: 5,
    title: 'New Product Drop',
    type: 'campaign',
    status: 'active',
    date: '2025-07-20',
    brand_id: 2,
    brand_name: 'GreenThreads',
    results: { impressions: 32000, engagement: 9.8, conversions: 89 }
  }
];

function SuspireVisibilityCalendar() {
  const [activeTab, setActiveTab] = useState('calendar');
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Fetch calendar activities from API
  const fetchCalendarActivities = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('No token found, using demo data');
        setActivities(DEMO_ACTIVITIES);
        setLoading(false);
        return;
      }

      // Calculate date range (current month +/- 1 month for buffer)
      const startDate = new Date(currentYear, currentMonth - 1, 1);
      const endDate = new Date(currentYear, currentMonth + 2, 0);
      
      const formatDate = (date) => {
        return date.toISOString().split('T')[0];
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/core/calendar/activities/?start_date=${formatDate(startDate)}&end_date=${formatDate(endDate)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Calendar activities API response:', data);
        
        // Process activities to match expected format
        const processedActivities = (data.activities || []).map(activity => ({
          ...activity,
          // Convert ISO datetime to date string for filtering
          date: activity.date ? activity.date.split('T')[0] : activity.date,
          // Convert results array to object for backward compatibility
          results: activity.results && Array.isArray(activity.results) 
            ? activity.results.reduce((acc, result) => {
                acc[result.kpi_name] = result.value;
                return acc;
              }, {})
            : activity.results
        }));
        
        setActivities(processedActivities);
      } else {
        console.error('Failed to fetch calendar activities:', response.status);
        setActivities(DEMO_ACTIVITIES);
      }
    } catch (error) {
      console.error('Error fetching calendar activities:', error);
      setActivities(DEMO_ACTIVITIES);
    } finally {
      setLoading(false);
    }
  }, [currentYear, currentMonth]);

  useEffect(() => {
    fetchCalendarActivities();
  }, [fetchCalendarActivities]);

  // Helper function to get activities for a specific date
  const getActivitiesByDate = (dateKey) => {
    return activities.filter(activity => {
      // Handle both ISO datetime and date string formats
      const activityDate = activity.date ? activity.date.split('T')[0] : activity.date;
      return activityDate === dateKey;
    });
  };

  // Helper function to handle activity click
  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'active': return '#FF9800';
      case 'scheduled': return '#2196F3';
      case 'paused': return '#9E9E9E';
      default: return '#757575';
    }
  };

  // Helper function to format activity type
  const formatActivityType = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const totalDays = new Date(currentYear, currentMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  const goToToday = () => {
    const now = new Date();
    setCurrentYear(now.getFullYear());
    setCurrentMonth(now.getMonth());
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let day = 1; day <= totalDays; day++) cells.push(day);

  // Render activities list for the current month
  const renderActivitiesList = () => {
    const currentMonthActivities = activities.filter(activity => {
      // Handle both ISO datetime and date string formats
      const activityDate = new Date(activity.date ? activity.date.split('T')[0] : activity.date);
      return activityDate.getFullYear() === currentYear && 
             activityDate.getMonth() === currentMonth;
    });

    if (currentMonthActivities.length === 0) {
      return (
        <div className={styles.noActivities}>
          <div className={styles.noActivitiesIcon}>📅</div>
          No activities for this month
        </div>
      );
    }

    const groupedByDate = currentMonthActivities.reduce((acc, activity) => {
      // Use processed date string for grouping
      const dateKey = activity.date ? activity.date.split('T')[0] : activity.date;
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(activity);
      return acc;
    }, {});

    return Object.keys(groupedByDate)
      .sort()
      .map(date => (
        <div key={date} className={styles.eventListDate}>
          <h4>{new Date(date).toLocaleDateString('en-US', { 
            weekday: 'short', 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          })}</h4>
          {groupedByDate[date].map(activity => (
            <div 
              key={activity.id} 
              className={styles.eventItem}
              onClick={() => handleActivityClick(activity)}
              style={{ cursor: 'pointer' }}
            >
              <div className={styles.activityHeader}>
                <strong>{activity.brand_name}</strong>
                <span 
                  className={styles.statusBadge}
                  style={{ backgroundColor: getStatusColor(activity.status) }}
                >
                  {activity.status}
                </span>
              </div>
              <div className={styles.activityTitle}>{activity.title}</div>
              <div className={styles.activityType}>{formatActivityType(activity.type)}</div>
            </div>
          ))}
        </div>
      ));
  };

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tabBtn} ${activeTab === 'calendar' ? styles.active : ''}`}
            onClick={() => setActiveTab('calendar')}
          >Calendar View</button>
          <button
            className={`${styles.tabBtn} ${activeTab === 'list' ? styles.active : ''}`}
            onClick={() => setActiveTab('list')}
          >Event List</button>
        </div>
        <div className={styles.monthNavigation}>
          <button onClick={prevMonth} className={styles.navBtn}>&#8249;</button>
          <h1>{monthNames[currentMonth]} {currentYear}</h1>
          <button onClick={nextMonth} className={styles.navBtn}>&#8250;</button>
          <button onClick={goToToday} className={styles.todayBtn}>Today</button>
        </div>
      </div>
      {activeTab === 'calendar' ? (
        <div className={styles.calendarGrid}>
          <div className={styles.calendarHeader}>Sun</div>
          <div className={styles.calendarHeader}>Mon</div>
          <div className={styles.calendarHeader}>Tue</div>
          <div className={styles.calendarHeader}>Wed</div>
          <div className={styles.calendarHeader}>Thu</div>
          <div className={styles.calendarHeader}>Fri</div>
          <div className={styles.calendarHeader}>Sat</div>
          {cells.map((day, idx) => {
            const dateKey = day
              ? `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
              : null;
            const dayActivities = dateKey ? getActivitiesByDate(dateKey) : [];
            return (
              <div key={idx} className={styles.dayCell}>
                {day && <div className={styles.dayNumber}>{day}</div>}
                <div className={styles.eventsContainer}>
                  {dayActivities.map((activity) => (
                    <div 
                      key={activity.id} 
                      className={styles.eventTile}
                      onClick={() => handleActivityClick(activity)}
                      style={{ 
                        cursor: 'pointer',
                        borderLeft: `3px solid ${getStatusColor(activity.status)}`
                      }}
                    >
                      <div className={styles.eventBrand}>{activity.brand_name}</div>
                      <div className={styles.eventName}>{activity.title}</div>
                      <div className={styles.eventType}>{formatActivityType(activity.type)}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.eventList}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.loadingSpinner}></div>
              Loading activities...
            </div>
          ) : (
            renderActivitiesList()
          )}
        </div>
      )}
      {/* Activity Detail Modal */}
      {selectedActivity && (
        <div className={styles.editModalOverlay} onClick={() => setSelectedActivity(null)}>
          <div className={styles.editModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{selectedActivity.title}</h3>
              <button 
                className={styles.closeBtn} 
                onClick={() => setSelectedActivity(null)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.activityInfo}>
                <div className={styles.infoRow}>
                  <strong>Brand:</strong> {selectedActivity.brand_name}
                </div>
                <div className={styles.infoRow}>
                  <strong>Type:</strong> {formatActivityType(selectedActivity.type)}
                </div>
                <div className={styles.infoRow}>
                  <strong>Status:</strong> 
                  <span 
                    className={styles.statusBadge}
                    style={{ backgroundColor: getStatusColor(selectedActivity.status) }}
                  >
                    {selectedActivity.status}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <strong>Date:</strong> {new Date(selectedActivity.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
              
              {selectedActivity.results && Object.keys(selectedActivity.results).length > 0 && (
                <div className={styles.resultsSection}>
                  <h4>Performance Metrics</h4>
                  <div className={styles.kpiGrid}>
                    {Object.entries(selectedActivity.results).map(([key, value]) => (
                      <div key={key} className={styles.kpiCard}>
                        <div className={styles.kpiValue}>
                          {value?.toLocaleString ? value.toLocaleString() : value || 'N/A'}
                        </div>
                        <div className={styles.kpiLabel}>
                          {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuspireVisibilityCalendar;
